/**
 * ThemeService
 * 
 * Service for fetching and updating user theme preferences from the backend.
 */

import { ThemeId, DEFAULT_THEME } from "../themes/tokens";

const SERVER_URL = process.env.REACT_APP_SERVER_URL || "http://localhost:2309";

export interface UserThemeResponse {
  theme: ThemeId;
}

class ThemeService {
  /**
   * Fetches the current user's theme preference
   * 
   * @param token - Authentication token (optional, will use authService if not provided)
   * @returns Promise<ThemeId> - The user's theme preference or default theme
   */
  async getUserTheme(token?: string): Promise<ThemeId> {
    const authToken = token || this.getAuthToken();
    
    if (!authToken) {
      // Guest users get default theme
      return DEFAULT_THEME;
    }

    try {
      const response = await fetch(`${SERVER_URL}/auth/me`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });

      if (!response.ok) {
        console.warn("[ThemeService] Failed to fetch user theme, using default");
        return DEFAULT_THEME;
      }

      const data = await response.json();
      const themeId = data.user?.theme as ThemeId | undefined;
      
      // Validate themeId
      if (themeId && this.isValidThemeId(themeId)) {
        return themeId;
      }
      
      return DEFAULT_THEME;
    } catch (error) {
      console.error("[ThemeService] Error fetching user theme:", error);
      return DEFAULT_THEME;
    }
  }

  /**
   * Updates the user's theme preference
   * 
   * @param themeId - The theme to set
   * @param token - Authentication token (optional)
   * @returns Promise<boolean> - Success status
   */
  async updateUserTheme(themeId: ThemeId, token?: string): Promise<boolean> {
    const authToken = token || this.getAuthToken();
    
    if (!authToken) {
      console.warn("[ThemeService] Cannot update theme: not authenticated");
      return false;
    }

    if (!this.isValidThemeId(themeId)) {
      console.error("[ThemeService] Invalid themeId:", themeId);
      return false;
    }

    try {
      const response = await fetch(`${SERVER_URL}/auth/me/theme`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({ theme: themeId }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: "Unknown error" }));
        console.error("[ThemeService] Failed to update theme:", error);
        return false;
      }

      return true;
    } catch (error) {
      console.error("[ThemeService] Error updating user theme:", error);
      return false;
    }
  }

  /**
   * Validates if a themeId is valid
   */
  private isValidThemeId(themeId: unknown): themeId is ThemeId {
    return (
      typeof themeId === "string" &&
      (themeId === "classic-dark" || themeId === "neon-arena" || themeId === "casual-party")
    );
  }

  /**
   * Gets auth token from localStorage
   */
  private getAuthToken(): string | null {
    return localStorage.getItem("uno_auth_token");
  }
}

export const themeService = new ThemeService();
